/**
 * Rasterises the SVG-rendered result charts to PNG data URLs so the exported
 * PPTX matches exactly what the user sees in the Results UI.
 *
 * The Plots and Usercharts components render a single `<si-chart>` each (SVG
 * renderer). We clone the live SVG, force light-theme colours (mirroring the
 * existing "Download Chart as SVG" logic), draw it onto a white canvas and read
 * back a PNG data URL.
 */

export interface CapturedChart {
  id: string;
  title: string;
  sourceSection: 'plots' | 'usercharts';
  pngBase64: string;
  widthPx: number;
  heightPx: number;
}

/** Pixel upscaling factor for crisper images inside the slide. */
const RENDER_SCALE = 2;

const cloneSvgForLightExport = (svg: SVGElement): SVGElement => {
  const clone = svg.cloneNode(true) as SVGElement;

  clone.querySelectorAll('text, tspan').forEach(textEl => {
    (textEl as SVGTextElement).setAttribute('fill', '#000000');
    (textEl as SVGTextElement).style.fill = '#000000';
  });

  clone.querySelectorAll('path, line').forEach(pathEl => {
    const stroke = (pathEl as SVGElement).getAttribute('stroke');
    if (stroke && (stroke.includes('#fff') || stroke.includes('white'))) {
      (pathEl as SVGElement).setAttribute('stroke', '#333333');
    }
  });

  return clone;
};

const loadImage = (url: string): Promise<HTMLImageElement> => {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error('Failed to rasterise chart SVG'));
    image.src = url;
  });
};

const svgElementToPng = async (
  svg: SVGElement
): Promise<{ pngBase64: string; widthPx: number; heightPx: number }> => {
  const rect = svg.getBoundingClientRect();
  const baseWidth = Math.max(1, Math.round(rect.width));
  const baseHeight = Math.max(1, Math.round(rect.height));

  const clone = cloneSvgForLightExport(svg);
  clone.setAttribute('width', String(baseWidth));
  clone.setAttribute('height', String(baseHeight));

  const svgString = new XMLSerializer().serializeToString(clone);
  const url = URL.createObjectURL(new Blob([svgString], { type: 'image/svg+xml;charset=utf-8' }));

  try {
    const image = await loadImage(url);
    const canvas = document.createElement('canvas');

    const maxDimension = 1920;
    let canvasWidth = Math.round(baseWidth * RENDER_SCALE);
    let canvasHeight = Math.round(baseHeight * RENDER_SCALE);

    if (canvasWidth > maxDimension || canvasHeight > maxDimension) {
      const scale = maxDimension / Math.max(canvasWidth, canvasHeight);
      canvasWidth = Math.round(canvasWidth * scale);
      canvasHeight = Math.round(canvasHeight * scale);
    }

    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    const context = canvas.getContext('2d');
    if (!context) {
      throw new Error('Canvas 2D context unavailable');
    }
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);

    return {
      pngBase64: canvas.toDataURL('image/png'),
      widthPx: canvas.width,
      heightPx: canvas.height
    };
  } finally {
    URL.revokeObjectURL(url);
  }
};

/**
 * Captures the currently rendered Plots and Usercharts charts from the given
 * host element. Charts that are not present or fail to rasterise are skipped.
 */
export const captureVisibleCharts = async (host: HTMLElement): Promise<CapturedChart[]> => {
  const targets: {
    id: string;
    title: string;
    section: 'plots' | 'usercharts';
    selector: string;
  }[] = [
    {
      id: 'plot_chart',
      title: 'Simulation Plot',
      section: 'plots',
      selector: 'app-plots si-chart svg'
    },
    {
      id: 'userchart',
      title: 'Custom Metrics Chart',
      section: 'usercharts',
      selector: 'app-usercharts si-chart svg'
    }
  ];

  const charts: CapturedChart[] = [];
  for (const target of targets) {
    const svg = host.querySelector<SVGElement>(target.selector);
    if (!svg) {
      continue;
    }
    try {
      const { pngBase64, widthPx, heightPx } = await svgElementToPng(svg);
      charts.push({
        id: target.id,
        title: target.title,
        sourceSection: target.section,
        pngBase64,
        widthPx,
        heightPx
      });
    } catch {
      // Skip charts that cannot be rasterised; the export still succeeds.
    }
  }
  return charts;
};
